import type { QueuedAudioFrame } from '@mimir/cutter/player/cutterCorePlayer';
import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import type { AudioWorkletRequest, AudioWorkletResponse } from './audioWorkletProtocol';
import type { AudioFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';

import workletUrl from './audioWorkletProcessor.ts?worker&url';
import { setSafariAudioSessionType } from '@mimir/browser/gldraw/browserQuirks';

interface Handlers {
  /**
   * Invoked when the audio samples actually start to be hearable
   * out of the speakers/headset.
   */
  onAudioFrameStartPlaying: () => void,

  /**
   * Invoked when the audio frame has been buffered for playout by
   * the audio worklet, which signals that we can buffer the next frame
   * if needed.
   */
  onAudioBufferDequeued: () => void,

  onCancelledPlayout: () => void,
}

export class WorkletAudioHandler implements AudioHandler {
  private bufferIdCounter : number = 0;
  private handlers : Record<string, Handlers> = { };
  private context = new AudioContext({sampleRate: 48000});
  private audioWorklet : AudioWorkletNode | null = null;
  private pendingRequests : AudioWorkletRequest[] = [];

  constructor() {
    /*  Safari has a bug where the AudioContext gets tied to the ringer volume instead of
     *  the media volume. The workaround (since iOS 17) is to explicitly set what kind of
     *  audio session it is for the page. If you are reading this and trying to fix an audio
     *  issue for earlier versions the previous workaround was to do this:
     *
     *  var el = document.createElement( 'audio' );
     *  el.src = 'silence.mp3'; // media file with tiny bit of silence
     *  el.play();
     *
     *  .. which of course is horrendous
     */
    setSafariAudioSessionType(navigator, 'playback');

    /**
     *  Vite has special handling for worker source code, but it only matches source that
     *  starts with new "Worker(new URL". Since the AudioWorklet API uses addModule instead
     *  of new Worker we need to trick vite with this dummy Worker class. Try to beat this
     *  hack.
     */
    if (this.context.sampleRate !== 48000) {
      throw new Error('AudioContext sample rate must be 48000 Hz, but was ' + this.context.sampleRate);
    }
    if (!this.context.audioWorklet) {
      throw new Error('AudioContext not supported in this context');
    }

    this.context.audioWorklet.addModule(workletUrl)
      .then(() => this.initialize());

    document.addEventListener('click', () => {
      if (this.context.state === 'suspended') {
        this.context.resume().then(() => {
        });
      }
    });
    document.addEventListener('keydown', () => {
      if (this.context.state === 'suspended') {
        this.context.resume().then(() => {
        });
      }
    });
  }

  private initialize() {
    this.audioWorklet = new AudioWorkletNode(this.context, 'cutterAudioWorkletProcessor', { outputChannelCount: [2] });
    this.audioWorklet.connect(this.context.destination);
    this.audioWorklet.port.onmessage = (msg: MessageEvent<AudioWorkletResponse>) => {
      const handler = this.handlers[msg.data.bufferId];
      if (handler !== undefined) {
        switch (msg.data.type) {
          case 'buffer_dequeued': {
            handler.onAudioBufferDequeued();
            break;
          }

          case 'start_playing_buffer': {
            delete this.handlers[msg.data.bufferId];
            handler.onAudioFrameStartPlaying();
            break;
          }

          case 'cancel_buffer': {
            delete this.handlers[msg.data.bufferId];
            handler.onCancelledPlayout();
            break;
          }
        }
      } else {
        throw new Error('No such buffer id: ' + msg.data.bufferId);
      }
    };

    for (const request of this.pendingRequests.splice(0)) {
      this.sendRequest(request);
    }
    this.context.resume();
  }

  close(): void {
    this.context.close();
  }

  queueAudioFrame(audioFrame: QueuedAudioFrame): void {
    let samples = getFloatSamplesFromAudioFrame(audioFrame.audioFrame);
    const bufferId = this.bufferIdCounter.toString();

    if (audioFrame.audioFrame.channelCount !== 2) {
      throw new Error(`Only stereo is supported. Got audio frame with ${audioFrame.audioFrame.channelCount} channels.`);
    }

    this.bufferIdCounter += 1;
    this.handlers[bufferId] = {
      onAudioFrameStartPlaying: audioFrame.onAudioFrameStartPlaying,
      onAudioBufferDequeued: audioFrame.onAudioBufferDequeued,
      onCancelledPlayout: audioFrame.onCancelledPlayout,
    };

    audioFrame.audioFrame.buffer.close();

    const latencyInSeconds = this.context.outputLatency ?? 0;
    const latencyInSamples = Math.round(latencyInSeconds * this.context.sampleRate);
    this.sendRequest({ type: 'frame', bufferId, samples, latencyInSamples });
  }

  startAudioPlayback(): void {
    this.sendRequest({ type: 'start_playback' });
  }

  stopAudioPlayback(clearQueue: boolean): void {
    this.sendRequest({ type: 'stop_playback', clearQueue });
  }

  sendRequest(req: AudioWorkletRequest) {
    if (this.audioWorklet !== null) {
      this.audioWorklet.port.postMessage(req);
    } else {
      this.pendingRequests.push(req);
    }
  }
}

function getFloatSamplesFromAudioFrame(frame: AudioFrameV2): number[] {
  switch (frame.format) {
    case 'pcm16': {
      const inSamples = frame.buffer.asReadonlyInt16Array();
      const outSamples : number[] = Array.from({ length: inSamples.length });
      // eslint-disable-next-line no-bitwise
      const factor = 1.0 / (1 << 15);
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i] * factor;
      }
      return outSamples;
    }
    case 'float32': {
      const inSamples = frame.buffer.asReadonlyFloat32Array();
      const outSamples : number[] = Array.from({ length: inSamples.length });
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i];
      }
      return outSamples;
    }
  }
}

